import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiChevronDown } = FiIcons;

const usaWhiteStripes = [2, 6, 10, 14];
const usaStarPositions = [
  { cx: 2.5, cy: 2.5 },
  { cx: 4.5, cy: 2.5 },
  { cx: 6.5, cy: 2.5 },
  { cx: 2.5, cy: 4.5 },
  { cx: 4.5, cy: 4.5 },
  { cx: 6.5, cy: 4.5 },
  { cx: 2.5, cy: 6.5 },
  { cx: 4.5, cy: 6.5 },
  { cx: 6.5, cy: 6.5 }
];

const flagIcons = {
  de: ({ className }) => (
    <svg
      viewBox="0 0 24 16"
      className={className}
      role="img"
      aria-label="Flagge von Deutschland"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="5.333" fill="#000" />
      <rect y="5.333" width="24" height="5.333" fill="#dd0000" />
      <rect y="10.666" width="24" height="5.333" fill="#ffce00" />
    </svg>
  ),
  us: ({ className }) => (
    <svg
      viewBox="0 0 24 16"
      className={className}
      role="img"
      aria-label="Flagge der Vereinigten Staaten"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="16" fill="#b22234" />
      {usaWhiteStripes.map((y) => (
        <rect key={`stripe-${y}`} x="0" y={y} width="24" height="1.8" fill="#fff" />
      ))}
      <rect width="10" height="7" fill="#3c3b6e" />
      {usaStarPositions.map((star, index) => (
        <circle key={`us-star-${index}`} cx={star.cx} cy={star.cy} r="0.6" fill="#fff" />
      ))}
    </svg>
  ),
  se: ({ className }) => (
    <svg
      viewBox="0 0 24 16"
      className={className}
      role="img"
      aria-label="Flagge von Schweden"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="16" fill="#0057b8" />
      <rect x="0" y="6" width="24" height="4" fill="#ffc72c" />
      <rect x="9" y="0" width="4" height="16" fill="#ffc72c" />
    </svg>
  ),
  hu: ({ className }) => (
    <svg
      viewBox="0 0 24 16"
      className={className}
      role="img"
      aria-label="Flagge von Ungarn"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="5.333" fill="#008742" />
      <rect y="5.333" width="24" height="5.333" fill="#fff" />
      <rect y="10.666" width="24" height="5.334" fill="#ce1126" />
    </svg>
  )
};

const languages = [
  { code: 'de', name: 'Deutsch', Flag: flagIcons.de },
  { code: 'en', name: 'English', Flag: flagIcons.us },
  { code: 'sv', name: 'Svenska', Flag: flagIcons.se },
  { code: 'hu', name: 'Magyar', Flag: flagIcons.hu }
];

const LanguageSelector = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200 border border-gray-300 rounded-md hover:border-blue-300"
      >
        <span className="flex items-center justify-center">
          {currentLang?.Flag ? (
            <currentLang.Flag className="h-5 w-6" />
          ) : (
            <FiGlobe className="h-5 w-5 text-gray-500" aria-hidden="true" />
          )}
        </span>
        <span className="hidden sm:inline">{currentLang?.name}</span>
        <SafeIcon icon={FiChevronDown} className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`flex items-center space-x-3 w-full px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors duration-200 ${
                  currentLanguage === language.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                <span className="flex items-center justify-center">
                  {language.Flag ? (
                    <language.Flag className="h-5 w-6" />
                  ) : (
                    <FiGlobe className="h-5 w-5 text-gray-500" aria-hidden="true" />
                  )}
                </span>
                <span>{language.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;